import { useState, useRef, useEffect } from "react";
import { Search, User, Users, Mail, UserCheck, CreditCard, List, Brain, Settings, LogOut } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { useSidebar } from "./ui/useSidebar";
import { useAuth } from "../hooks/useAuth";
import { ThemeToggle } from "./ui/themeToggle";
import { useTheme } from "next-themes";
import { secureStorage } from "@/utils/secureStorage";


interface HeaderProps {
  onSectionChange?: (section: string) => void;
  onLogout?: () => void;
}

export function Header({ onSectionChange, onLogout }: HeaderProps) {
  const { toggleSidebar, state } = useSidebar();
  const { user } = useAuth();
  const { theme } = useTheme();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  //const currentUserProfilePic = secureStorage.get("current_user_pic");
  const currentUserProfilePic =
  secureStorage.get<string>("current_user_pic") ?? undefined;
  const logoSrc = theme === "dark" ? "/logo_white.png" : "/logo.png";
  const userType = secureStorage.get("user_type");

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleMenuItemClick = (section: string) => {
    setIsDropdownOpen(false);
    if (section === "logout" && onLogout) {
      onLogout();
    } else if (onSectionChange) {
      onSectionChange(section);
    }
  };
  
  return (
    <header className="glass-header sticky top-0 z-50 w-full">
      <div className="flex h-16 items-center px-6 w-full">
        {/* Clickable Logo - Always show on smaller screens, conditional on desktop */}
        <button
          onClick={toggleSidebar}
          className={`flex items-center hover:opacity-80 transition-opacity cursor-pointer ${
            state === "collapsed" ? "flex" : "xl:hidden"
          }`}
        >
          <img
            src={logoSrc}
            alt="Swimnerd Logo"
            className="h-12 w-auto"
          />
        </button>

        {/* Search Bar */}
        <div className="flex-1 mx-8">
          <div className="relative max-w-2xl">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search workouts, sets, techniques..."
              className="pl-10 bg-secondary/50 w-full"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-2">
           {/* Notification Button */}
            <Button
              variant="ghost"
              size="icon"
              className="relative rounded-full hover:bg-accent"
              onClick={() => handleMenuItemClick("notification")}
            >
              <div className="relative">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002
                      6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 
                      6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 
                      1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  />
                </svg>

                {/* Notification dot */}
                <span className="absolute top-0 right-0 h-2 w-2 bg-red-500 rounded-full"></span>
              </div>
            </Button>
          
          {/* Theme Toggle */}
          <ThemeToggle />
          
          {/* Profile Dropdown - Simple Test */}
          <div className="relative" ref={dropdownRef}>
            <Button 
              variant="ghost" 
              size="icon" 
              className="rounded-full hover:bg-accent"
              onClick={toggleDropdown}
            >
              <Avatar className="h-8 w-8">
                {/* <AvatarImage src="/placeholder-avatar.jpg" /> */}
                 <AvatarImage src={currentUserProfilePic || undefined} />
                <AvatarFallback>
                {(user as any)?.initials ?? "U"}
              </AvatarFallback>
              </Avatar>
            </Button>
            
            {/* Simple test dropdown */}
            {isDropdownOpen && (
              <div 
                className="absolute right-0 top-full mt-2 w-56 glass-dropdown rounded-lg z-[9999]"
                style={{
                  zIndex: 9999,
                  position: 'absolute'
                }}
              >
                <div className="p-2">
                  <div className="px-3 py-2 text-sm font-medium text-card-foreground">My Account</div>
                  <div className="border-t border-border"></div>
                  
                  <div 
                    className="px-3 py-2 text-sm text-card-foreground hover:bg-accent cursor-pointer flex items-center"
                    onClick={() => handleMenuItemClick("my-profile")}
                  >
                    <User className="mr-2 h-4 w-4" />
                    My Profile
                  </div>
                  
                  {userType !== "swimmer" && (
                  <div 
                    className="px-3 py-2 text-sm text-card-foreground hover:bg-accent cursor-pointer flex items-center"
                    onClick={() => handleMenuItemClick("change-swimmer")}
                  >
                    <Users className="mr-2 h-4 w-4" />
                    Change Swimmer
                  </div>
                  )}
                  
                  <div 
                    className="px-3 py-2 text-sm text-card-foreground hover:bg-accent cursor-pointer flex items-center"
                    onClick={() => handleMenuItemClick("inbox")}
                  >
                    <Mail className="mr-2 h-4 w-4" />
                    Inbox
                  </div>
                  
                  {userType !== "swimmer" && (
                    <div 
                      className="px-3 py-2 text-sm text-card-foreground hover:bg-accent cursor-pointer flex items-center"
                      onClick={() => handleMenuItemClick("apply-to-coach")}
                    >
                      <UserCheck className="mr-2 h-4 w-4" />
                      Apply To Coach
                    </div>
                  )}
                  
                  {userType !== "swimmer" && (
                  <div 
                    className="px-3 py-2 text-sm text-card-foreground hover:bg-accent cursor-pointer flex items-center"
                    onClick={() => handleMenuItemClick("payout-details")}
                  >
                    <CreditCard className="mr-2 h-4 w-4" />
                    Payout Details
                  </div>
                   )}
                   
                  <div 
                    className="px-3 py-2 text-sm text-card-foreground hover:bg-accent cursor-pointer flex items-center"
                    onClick={() => handleMenuItemClick("change-password")}
                  >
                    <Settings className="mr-2 h-4 w-4" />
                    Change Password
                  </div>
                  
                  <div 
                    className="px-3 py-2 text-sm text-card-foreground hover:bg-accent cursor-pointer flex items-center"
                    onClick={() => handleMenuItemClick("payment-methods")}
                  >
                    <CreditCard className="mr-2 h-4 w-4" />
                    Payment Methods
                  </div>
                  
                  {userType !== "swimmer" && (
                      <div 
                        className="px-3 py-2 text-sm text-card-foreground hover:bg-accent cursor-pointer flex items-center"
                        onClick={() => handleMenuItemClick("your-listings")}
                      >
                        <List className="mr-2 h-4 w-4" />
                        Your Listings
                      </div>
                    )}
                  
                  <div 
                    className="px-3 py-2 text-sm text-card-foreground hover:bg-accent cursor-pointer flex items-center"
                    onClick={() => handleMenuItemClick("ai-knowledge")}
                  >
                    <Brain className="mr-2 h-4 w-4" />
                    AI Knowledge
                  </div>
                   
                  <div 
                    className="px-3 py-2 text-sm text-card-foreground hover:bg-accent cursor-pointer flex items-center"
                    onClick={() => handleMenuItemClick("settings")}
                  >
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </div>
                  
                  <div className="border-t border-gray-100"></div>
                  <div 
                    className="px-3 py-2 text-sm text-red-600 hover:bg-gray-100 cursor-pointer flex items-center"
                    onClick={() => handleMenuItemClick("logout")}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}